// Entry screen: logogram intro (240 gold particles converge along the mark
// outline, the mark fills cream, done at 2.7 s) + edition, "We take your
// order at the table.", the prices note and the scroll cue. Port of the
// prototype's entry section (37–47) and drawIntro (615–625).
import { useEffect, useRef } from 'react';
import { MARK } from '../lib/mark-outline.js';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const easeOut = (x) => 1 - Math.pow(1 - x, 3);

const mono = { fontFamily: "'DM Mono', 'DM Mono Fallback', ui-monospace, monospace" };

// deep — the page landed on a deep link: the entry is scrolled past, so its
// logotype (the entry's LCP on "/") must not share the wire with that landing.
export default function Entry({ T, rm, skipIntro, deep }) {
  const secRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const c = canvasRef.current, sec = secRef.current;
    if (!c || !sec) return undefined;
    const ctx = c.getContext('2d');
    const S = c.width, cx = S / 2, cy = S / 2, R = S * 0.46;
    const path = new Path2D(MARK.svgPath);
    const drawMark = (alpha) => {
      ctx.save();
      ctx.translate(cx, cy); ctx.scale(R / 480, R / 480); ctx.translate(-500, -500);
      ctx.globalAlpha = alpha; ctx.fillStyle = '#f4f0e8'; ctx.fill(path);
      ctx.restore();
    };
    if (rm || skipIntro.current) {
      ctx.clearRect(0, 0, S, S); drawMark(1); sec.dataset.on = '1';
      return undefined;
    }
    let raf = 0, t0 = 0, alive = true;
    const N = 240, n = MARK.n;
    const frame = (now) => {
      if (!alive) return;
      if (!t0) t0 = now;
      const t = now - t0;
      ctx.fillStyle = 'rgba(7,30,33,0.32)'; ctx.fillRect(0, 0, S, S);
      const fillA = clamp((t - 900) / 1000, 0, 1);
      if (fillA > 0) drawMark(fillA);
      if (t < 2300) {
        for (let i = 0; i < N; i++) {
          const th = (i / N) * Math.PI * 2;
          let u = (th / (Math.PI * 2)) % 1; if (u < 0) u += 1;
          const f = u * n, i0 = Math.floor(f) % n, i1 = (i0 + 1) % n, ft = f - Math.floor(f);
          const rr = MARK.r[i0] * (1 - ft) + MARK.r[i1] * ft;
          const tx = cx + Math.cos(th) * rr * R, ty = cy - Math.sin(th) * rr * R;
          const j = (Math.sin(i * 12.9898) * 43758.5453) % 1;
          const sx = cx + Math.cos(th + j * 0.6) * R * 1.75, sy = cy - Math.sin(th + j * 0.6) * R * 1.75;
          const k = easeOut(clamp((t - (i % 7) * 60) / 1300, 0, 1));
          const x = sx + (tx - sx) * k, y = sy + (ty - sy) * k;
          const a = clamp((t - 40) / 300, 0, 1) * (1 - clamp((t - 1700) / 600, 0, 1));
          ctx.globalAlpha = a * (0.5 + 0.5 * Math.abs(j));
          ctx.fillStyle = '#e8b86b';
          ctx.fillRect(x - 1.2, y - 1.2, 2.4, 2.4);
        }
        ctx.globalAlpha = 1;
      }
      if (t > 1300 && sec.dataset.on !== '1') sec.dataset.on = '1';
      if (t > 2700) { ctx.clearRect(0, 0, S, S); drawMark(1); return; }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [rm, skipIntro]);

  return (
    <section
      id="s-top"
      ref={secRef}
      data-tone="night"
      data-gl-view="dust"
      data-on="0"
      aria-label="Salésol"
      style={{ position: 'relative', minHeight: '100%', boxSizing: 'border-box', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '0 2em' }}
    >
      <div aria-hidden="true" style={{ flex: '1 1 0', minHeight: '.5em' }} />
      <canvas ref={canvasRef} data-mark width="480" height="480" aria-hidden="true" style={{ width: 'min(240px, calc(var(--vh, 844px) * .28))', height: 'min(240px, calc(var(--vh, 844px) * .28))', display: 'block' }} />
      <img src="/brand/salesol_logotype_white-sm.png" width="480" height="170" fetchpriority={deep ? undefined : 'high'} loading={deep ? 'lazy' : undefined} alt="salésol" style={{ height: 22, width: 'auto', display: 'block', marginTop: '1.2em' }} />
      <span className="beat" style={{ ...mono, fontSize: '.6875em', letterSpacing: '.12em', textTransform: 'uppercase', marginTop: '2em', transitionDelay: '.35s' }}>{T.edition}</span>
      <span className="beat" style={{ fontSize: '1.125em', marginTop: '2.2em', textWrap: 'balance', transitionDelay: '.75s' }}>{T.order}</span>
      <span className="beat" style={{ ...mono, fontSize: '.625em', letterSpacing: '.1em', textTransform: 'uppercase', color: '#b9c1bf', marginTop: '.8em', transitionDelay: '.9s' }}>{T.prices}</span>
      <div aria-hidden="true" style={{ flex: '1 1 0', minHeight: '1.2em' }} />
      <div className="beat" style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, paddingBottom: 30, transitionDelay: '1.3s' }}>
        <span style={{ ...mono, fontSize: '.625em', letterSpacing: '.14em', textTransform: 'uppercase', color: '#b9c1bf' }}>{T.scroll}</span>
        <span data-cue aria-hidden="true" style={{ display: 'block', width: 1, height: 40, background: '#e8b86b' }} />
      </div>
    </section>
  );
}
